import type { CellPosition } from "./GridCell";
import { NEW_ROW_ID } from "./newRowId";

export interface EditSelection {
    editingPos: CellPosition | null;
    focusPos: CellPosition[] | null;
    // The printable character the user typed to start editing, or null when
    // editing was entered by click / Enter. Popup-based cells (e.g. link cells)
    // read it to seed their search with that first character — otherwise the
    // keystroke that opens the popup is lost.
    editInitialKey: string | null;
}

function copy(pos: CellPosition | null): CellPosition | null {
    return pos ? { rowId: pos.rowId, column: pos.column } : null;
}

/**
 * Computes the focus/edit state for entering edit mode at `position`.
 *
 * `initialKey` carries the character that initiated the edit (null for
 * click/Enter). It is propagated through the store so a link cell can pre-fill
 * its relation search with the first typed letter instead of dropping it.
 */
export function enterEdit(position: CellPosition | null, initialKey: string | null = null): EditSelection {
    return {
        editingPos: copy(position),
        focusPos: position ? [copy(position)!] : null,
        editInitialKey: initialKey,
    };
}

export interface NewRowColumn {
    readOnly: boolean;
    // A checkbox or a button is used in place: the cell never enters the edit mode
    noEditor: boolean;
}

/**
 * Computes the cell the creation row opens on: the first one which can be modified.
 * Editing a read-only cell shows no input, so the DOM focus would fall to the body
 * and the keyboard would no longer reach the row.
 */
export function initialNewRowSelection(columns: NewRowColumn[]): { position: CellPosition; edit: boolean } {
    const column = columns.findIndex(newRowColumn => !newRowColumn.readOnly);
    if (column === -1) {
        return { position: { rowId: NEW_ROW_ID, column: 0 }, edit: false };
    }
    return { position: { rowId: NEW_ROW_ID, column }, edit: !columns[column].noEditor };
}
